import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import { useMemo } from "react";
import { Link, Outlet, useParams } from "react-router";

import { useAuth } from "@/providers";
import {
  ExpandableCopyText,
  PageBreadcrumbs,
  PageLayout,
  TagList,
} from "@/shared/components";
import { ErrorBoundary } from "@/shared/components/ErrorBoundary";
import { RobotoPrincipalUnderlineLink } from "@/shared/components/principal";
import { FormattedDateTime } from "@/shared/datetime";
import type { Device as DeviceRecord } from "@/shared/domain/devices";
import { useDevice } from "@/shared/domain/devices/hooks/useDevice";
import { useDeviceType } from "@/shared/domain/deviceTypes/hooks";
import { useCheckFeatureFlag } from "@/shared/domain/featureFlags/hooks";
import { Badge, Callout } from "@/shared/foundation/primitives";
import { Goto } from "@/shared/navigation";
import { useRegisterViewingContext } from "@/shared/services/ViewingContextService";

import { DeviceTabs } from "../DeviceTabs";

import styles from "./Device.module.css";
import type { DeviceContext } from "./useDeviceContext";

/**
 * Parent layout for the `/devices/:deviceId` route tree. Renders the page
 * chrome (breadcrumb, header block and tab bar) and delegates the active
 * tab's body to `<Outlet>`. Publishes `{ deviceId, org }` as outlet context so
 * descendant tab routes can read them via `useDeviceContext` instead of
 * re-deriving from `useParams` / `useAuth`.
 *
 * Renders an error in place of the chrome when the URL is missing
 * `:deviceId`; downstream errors thrown inside a tab are caught by the
 * shared `<ErrorBoundary>` wrapping the outlet.
 */
export function Device() {
  const { deviceId } = useParams();
  const { currentOrganization: org } = useAuth();

  // Memoize so the object handed to <Outlet context={...}> keeps a stable
  // reference across renders whose inputs haven't changed. Without this,
  // every render of <Device /> would publish a fresh context object and
  // invalidate referential-equality checks (memo deps, effect deps) in
  // descendant tabs that consume the bundle through useDeviceContext.
  const outletContext = useMemo<DeviceContext | null>(() => {
    if (!deviceId) {
      return null;
    }
    return {
      deviceId,
      org,
    };
  }, [deviceId, org]);

  // Publish the device to AI Chat so the agent can resolve "this device"
  // without the user pasting its id. Registered before the `!deviceId` guard
  // below so the hook runs on every render; an undefined id contributes
  // nothing. No `orgId`: a device is only loadable from within its owning
  // org, so there is never an org switch to offer (see `ViewingContextMap`).
  useRegisterViewingContext("device", { deviceId });

  // Guard: a route hit without `:deviceId` can't render the device chrome
  // or hydrate child tabs; surface the misconfiguration inline rather than
  // letting downstream hooks fire with an undefined id.
  if (!deviceId) {
    return (
      <PageLayout title="Roboto - Device">
        <Callout variant="error">Missing device id in path</Callout>
      </PageLayout>
    );
  }

  return (
    <PageLayout
      title={`Roboto - Device - ${deviceId}`}
      headerContentLeft={
        <PageBreadcrumbs>
          <Link to={Goto.route.Devices}>Devices</Link>
          <ExpandableCopyText text={deviceId} trimLength={36} />
        </PageBreadcrumbs>
      }
    >
      <ErrorBoundary
        fallback={(props) => {
          if (props.error instanceof Error) {
            return <Callout variant="error">{props.error.message}</Callout>;
          }

          return (
            <Callout variant="error">
              Something went wrong. Please contact{" "}
              <a href="mailto:support@roboto.ai">support@roboto.ai</a> if this
              problem persists.
            </Callout>
          );
        }}
      >
        <div className={styles.root}>
          <DeviceChrome deviceId={deviceId} orgId={org?.org_id} />
          <div className={styles.tabPanel}>
            <Outlet context={outletContext} />
          </div>
        </div>
      </ErrorBoundary>
    </PageLayout>
  );
}

/**
 * Title row, tags, metadata strip and tab bar for `/devices/:deviceId`. The
 * title is the device's ID, which is its name. The record's facts appear once
 * `useDevice` resolves (TanStack Query caches it, so the Details tab's own
 * call is a cache hit); the tab bar renders regardless, since it reads its
 * active value from the URL.
 */
function DeviceChrome({
  deviceId,
  orgId,
}: {
  deviceId: string;
  orgId: string | undefined;
}) {
  const device = useDevice(deviceId, orgId).data ?? null;

  return (
    <div className={styles.headerSection}>
      <div className={styles.titleRow}>
        <h1 className={styles.title}>{deviceId}</h1>
        {device && <DeviceTypeChip device={device} />}
      </div>

      {device && device.tags.length > 0 && (
        <div className={styles.tagsRow}>
          <TagList tags={device.tags} maxDisplayCount={5} />
        </div>
      )}

      {device && <DeviceMetadataRow device={device} />}

      <DeviceTabs />
    </div>
  );
}

/** Names the device's type and links to it. Hidden until the type is known. */
function DeviceTypeChip({ device }: { device: DeviceRecord }) {
  const assetStoreEnabled = useCheckFeatureFlag(
    "release-asset-store",
    device.org_id,
  );
  const deviceTypeId = assetStoreEnabled ? device.device_type_id : undefined;
  const deviceType = useDeviceType(deviceTypeId ?? undefined, device.org_id);
  if (!deviceTypeId) {
    return null;
  }
  return (
    <Link
      to={Goto.createDeviceTypeRoute(deviceTypeId)}
      className={styles.typeChip}
    >
      <Badge variant="secondary">{deviceType.data?.name ?? deviceTypeId}</Badge>
    </Link>
  );
}

function DeviceMetadataRow({ device }: { device: DeviceRecord }) {
  return (
    <div className={styles.metadataRow}>
      {device.universal_device_id && (
        <span className={styles.metadataField}>
          <span className={styles.metadataLabel}>Universal Device ID:</span>
          <span className={styles.metadataText}>
            {device.universal_device_id}
          </span>
        </span>
      )}

      <span className={styles.metadataField}>
        <CalendarTodayOutlinedIcon className={styles.metadataIcon} />
        <span className={styles.metadataLabel}>Created:</span>
        <span className={styles.metadataText}>
          <FormattedDateTime value={device.created} time="none" />
        </span>
      </span>

      <span className={styles.metadataField}>
        <span className={styles.metadataLabel}>Created By:</span>
        <RobotoPrincipalUnderlineLink principal={device.created_by} icon />
      </span>
    </div>
  );
}
